import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";

// Pages
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import LocationsPage from "@/pages/LocationsPage";
import LocationDetailPage from "@/pages/LocationDetailPage";
import FieldsPage from "@/pages/FieldsPage";
import CalendarsPage from "@/pages/CalendarsPage";
import UsersPage from "@/pages/UsersPage";
import OffboardingPage from "@/pages/OffboardingPage";
import ActivityPage from "@/pages/ActivityPage";
import LocationUsersPage from "@/pages/LocationUsersPage";
import SyncRequestsPage from "@/pages/SyncRequestsPage";
import SettingsPage from "@/pages/SettingsPage";
import TicketsInboxPage from "@/pages/TicketsInboxPage";
import ProfilePage from "@/pages/ProfilePage";


// Layout
import AppLayout from "@/components/layout/AppLayout";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";

const ProtectedRoute = ({ children, adminOnly = false, superAdminOnly = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (superAdminOnly && user.role !== 'super_admin' && user.role !== 'agency_admin') {
    return <Navigate to="/tickets" replace />;
  }

  if (adminOnly && !['admin', 'super_admin', 'agency_admin', 'agency_support', 'account_admin'].includes(user.role)) {
    return <Navigate to="/tickets" replace />;
  }

  return children;
};

function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/tickets" replace /> : <LoginPage />} />
      
      <Route path="/" element={
        <ProtectedRoute>
          <AppLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/tickets" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="locations" element={<LocationsPage />} />
        <Route path="locations/:locationId" element={<LocationDetailPage />} />
        <Route path="locations/:locationId/fields" element={<FieldsPage />} />
        <Route path="locations/:locationId/calendars" element={<CalendarsPage />} />
        <Route path="locations/:locationId/users" element={<LocationUsersPage />} />
        <Route
          path="users"
          element={
            <ProtectedRoute adminOnly>
              <UsersPage />
            </ProtectedRoute>
          }
        />
        <Route path="offboarding" element={<OffboardingPage />} />
        <Route path="activity" element={
          <ProtectedRoute superAdminOnly>
            <ActivityPage />
          </ProtectedRoute>
        } />

        <Route path="profile" element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        } />
        <Route path="tickets" element={<TicketsInboxPage />} />
        <Route path="sync-requests" element={<SyncRequestsPage />} />
        <Route path="settings" element={
          <ProtectedRoute superAdminOnly>
            <SettingsPage />
          </ProtectedRoute>
        } />
      </Route>

      <Route path="*" element={<Navigate to="/tickets" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
        <PWAInstallPrompt />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
